import React, { useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  MessageCircle,
  X,
  FileText,
  Download
} from 'lucide-react';
import { functionsAsia } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { Link } from "wouter";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";

const WHATSAPP_LINK = "https://whatsapp.com/channel/0029Vb7sQ6zFy727U66AhN2a";

export default function BetaTesterPromo() {
  // Form State
  const [showBetaForm, setShowBetaForm] = useState(false);
  const [betaName, setBetaName] = useState("");
  const [betaEmail, setBetaEmail] = useState("");
  const [betaReferralCode, setBetaReferralCode] = useState("");
  const [betaAgreed, setBetaAgreed] = useState(false);
  const [betaSubmitting, setBetaSubmitting] = useState(false);
  const [betaSuccess, setBetaSuccess] = useState(false);
  const [betaError, setBetaError] = useState("");

  const openBetaForm = () => {
    setBetaName("");
    setBetaEmail("");
    setBetaReferralCode("");
    setBetaAgreed(false);
    setBetaSubmitting(false);
    setBetaSuccess(false);
    setBetaError("");
    setShowBetaForm(true);
  };

  const handleBetaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBetaSubmitting(true);
    setBetaError("");
    try {
      const sendBetaEmail = httpsCallable(functionsAsia, 'triggerBetaTesterEmail');
      // Pass the referralCode along with name and email
      await sendBetaEmail({
        name: betaName,
        email: betaEmail,
        referralCode: betaReferralCode
      });

      setBetaSuccess(true);

      setTimeout(() => {
        setShowBetaForm(false);
        window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer");
      }, 1500);
    } catch {
      setBetaError("Something went wrong. Please try again.");
    } finally {
      setBetaSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">

      {/* ── IDENTICAL HEADER FROM MARKETING LANDING ── */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Logo size="md" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="default"
              size="sm"
              className="gap-1.5"
              onClick={openBetaForm}
              data-testid="link-header-download"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Get the App</span>
              <span className="sm:hidden">Download</span>
            </Button>
          </div>
        </div>
      </header>

      {/* ── MAIN PROMO CONTENT ── */}
      <main className="flex-1 pb-20">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto bg-card shadow-xl rounded-b-3xl overflow-hidden border-x border-b border-border">
          <div className="relative w-full aspect-[16/9] md:aspect-[21/9] bg-muted">
            <img
              src="/BetaTester-Voucher.jpg"
              alt="Earn ₹100 Amazon Voucher - DailyHisaab Beta Tester"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="p-8 md:p-12 text-center">
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              We are looking for dedicated Beta Testers to try out the app, push its limits, and help us squash any remaining bugs before our official Play Store launch.
            </p>
          </div>
        </div>

        {/* Content Container */}
        <div className="max-w-4xl mx-auto px-4 mt-8 space-y-8">

          {/* The 14-Day Challenge Rules */}
          <div className="bg-card rounded-3xl p-8 shadow-sm border border-border">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
              <h2 className="text-2xl font-bold text-foreground">The 14-Day Challenge Rules</h2>
            </div>
            <p className="text-muted-foreground mb-6 font-medium">
              To be eligible for the ₹100 Amazon Voucher, you must strictly follow these testing guidelines. Google monitors tester engagement closely, so partial participation will not count.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-amber-500/10 rounded-2xl border border-amber-500/20">
                <div className="bg-amber-500/20 p-2 rounded-full text-amber-700 dark:text-amber-400 mt-1">1</div>
                <div>
                  <h3 className="font-bold text-foreground text-lg">The Daily Streak (MANDATORY)</h3>
                  <p className="text-muted-foreground mt-1 leading-relaxed">
                    You must open the app and perform at least one genuine activity (e.g., adding a product, recording a sale, generating a bill) <strong>every single day for 14 consecutive days</strong>. If you skip even one day, you will be automatically disqualified.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-2xl border border-border">
                <div className="bg-muted p-2 rounded-full text-foreground mt-1">2</div>
                <div>
                  <h3 className="font-bold text-foreground text-lg">The Bug Hunt</h3>
                  <p className="text-muted-foreground mt-1 leading-relaxed">
                    You must find and report <strong>at least 3 bugs, glitches, or UI improvements</strong>. You must submit these reports using the "Need Help" section directly inside the app.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-2xl border border-border">
                <div className="bg-muted p-2 rounded-full text-foreground mt-1">3</div>
                <div>
                  <h3 className="font-bold text-foreground text-lg">Setup & Keep Installed</h3>
                  <p className="text-muted-foreground mt-1 leading-relaxed">
                    Complete your Business Profile inside the app right away. Do not uninstall the app from your primary device during the entire 14-day testing period.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="bg-card rounded-3xl p-8 shadow-sm border border-border">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="h-7 w-7 text-green-600" />
              <h2 className="text-xl font-bold text-foreground">Terms & Conditions</h2>
            </div>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span><strong>One Device per User:</strong> Only one registration per person/device will be accepted. Multiple accounts will lead to a ban.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span><strong>Constructive Feedback:</strong> Reports like "app not working" will not be counted. Provide detailed steps on what happened.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span><strong>Reward Distribution:</strong> Vouchers will be distributed in ₹25 + ₹25 + ₹50 order. Amazon vouchers can be redeem from Reward Points page in the app. Users will received the vouchers within 3-5 business days on their <em>registered email address</em> the successful completion of the 3 days, 7 days and  14-day testing phase.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span><strong>Admin Discretion:</strong> The team reserves the right to disqualify any tester who tries to game the system with fake data or inauthentic activity.</span>
              </li>
            </ul>
          </div>
          {/* Call to Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={openBetaForm}
              className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-4 px-8 rounded-xl shadow-lg transition-transform active:scale-95"
            >
              <FileText className="h-5 w-5" />
              <span>Register as a Tester</span>
            </button>

            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-transform active:scale-95"
            >
              <MessageCircle className="h-5 w-5" />
              <span>Follow on WhatsApp</span>
            </a>
          </div>
        </div>
      </main>

      {/* ── IDENTICAL FOOTER FROM MARKETING LANDING ── */}
      <footer className="border-t border-border py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Logo size="sm" />

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link
                href="/privacy"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Terms of Service
              </Link>
              <Link
                href="/data-deletion"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Data Deletion Policy
              </Link>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with care in Bengal by{" "}
                <a
                  href="https://asldigitalsolutions.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                &copy;2026 DailyHisaab. All Rights Reserved
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* ── BETA TESTER POPUP FORM ── */}
      {showBetaForm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-background rounded-2xl shadow-2xl border border-border overflow-hidden">
            <button
              onClick={() => setShowBetaForm(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="bg-primary px-6 pt-6 pb-5">
              <h2 className="text-xl font-bold text-primary-foreground">
                Join as Beta Tester
              </h2>
              <p className="text-sm text-primary-foreground/80 mt-1">
                Help us test the app and earn your ₹100 voucher!
              </p>
            </div>

            {betaSuccess ? (
              <div className="px-6 py-10 flex flex-col items-center text-center gap-3">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
                <p className="text-lg font-bold text-foreground">
                  Registration Successful!
                </p>
                <p className="text-sm text-muted-foreground">
                  Redirecting you to our WhatsApp Channel…
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleBetaSubmit}
                className="px-6 py-6 flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-sm font-medium text-foreground"
                    htmlFor="beta-name"
                  >
                    Your Name
                  </label>
                  <input
                    id="beta-name"
                    type="text"
                    required
                    placeholder="Enter your name"
                    value={betaName}
                    onChange={(e) => setBetaName(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-sm font-medium text-foreground"
                    htmlFor="beta-email"
                  >
                    Google Play Store Email
                  </label>
                  <input
                    id="beta-email"
                    type="email"
                    required
                    placeholder="Email linked to your Play Store account"
                    value={betaEmail}
                    onChange={(e) => setBetaEmail(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <p className="text-xs text-muted-foreground">
                    This email must be the exact one you use on your Android phone to download apps.
                  </p>
                </div>

                {/* ── ADD THIS NEW BLOCK ── */}
                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-sm font-medium text-foreground"
                    htmlFor="beta-referral"
                  >
                    Referral Code <span className="text-muted-foreground font-normal">(Optional)</span>
                  </label>
                  <input
                    id="beta-referral"
                    type="text"
                    placeholder="Enter referral code"
                    value={betaReferralCode}
                    onChange={(e) => setBetaReferralCode(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-primary/40 uppercase"
                  />
                </div>
                {/* ── END NEW BLOCK ── */}

                <label className="flex items-start gap-3 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    required
                    checked={betaAgreed}
                    onChange={(e) => setBetaAgreed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                  />
                  <span className="text-sm text-foreground leading-relaxed">
                    I agree to the 14-Day Challenge Rules and understand that missing a day of activity will disqualify me from the ₹100 reward.
                  </span>
                </label>

                {betaError && (
                  <p className="text-sm text-destructive font-medium">{betaError}</p>
                )}

                <button
                  type="submit"
                  disabled={
                    betaSubmitting ||
                    !betaName.trim() ||
                    !betaEmail.trim() ||
                    !betaAgreed
                  }
                  className="w-full py-3 mt-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {betaSubmitting
                    ? "Registering…"
                    : "Register & Follow on WhatsApp"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}